import { useChat } from "@ai-sdk/react"
import { type UIMessage } from "ai"
// ArrowUp/Square now live inside ChatBar; only the quote-chip icons remain here.
import { TextQuote, X } from "lucide-react"
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { createPortal } from "react-dom"
import { toast } from "sonner"
import { Link } from "@tanstack/react-router"
import { istAktion, istZiel, type CoachPrompt } from "@/lib/coach-prompts"
import { coachSuggestions } from "@/lib/coach-suggestions"

import { Button } from "@/components/ui/button"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { CoachBubble, partsToText } from "@/components/coach-bubble"
import { CoachThreadApiProvider } from "@/lib/coach-thread-api"
import {
  CoachToolCalls,
  CoachToolCards,
  hasToolCalls,
  hasToolCards,
} from "@/components/coach-tool-call"
import {
  CHAT_MODE_LABELS,
  initialsOf,
  type ChatMessage,
  type ChatMode,
  type ToolCallRecord,
} from "@nl/shared"
import { ChatBar } from "@/components/chat-bar"
import { composeQuotedMessage } from "@/lib/quote"
import { FallbackChatTransport } from "@/lib/socket/fallback-chat-transport"
import { useDictation } from "@/lib/use-dictation"
import { cn } from "@/lib/utils"

/**
 * Which conversation this thread is: the coach after a single phase, after a
 * whole run, or the open one. There is one coach with three context scopes, not
 * three coaches, so the scope only decides the endpoint and what the backend
 * loads as grounding.
 */
export type CoachThreadScope =
  | { kind: "open" }
  | { kind: "session"; sessionId: string }
  | { kind: "run"; runId: string }

/** What a caller rendered above the thread may do with it. */
export type CoachThreadApi = {
  ask: (text: string) => void
  focusComposer: () => void
  canSend: boolean
  isEmpty: boolean
  /**
   * Hat die Beraterin selbst schon etwas gesagt?
   *
   * Trägt die Einstiegsaktionen: sobald sie antwortet, läuft das Gespräch und
   * die Angebote darunter sind erledigt. Aus dem Verlauf, nicht aus dem
   * Server-Zähler, damit der Wechsel beim Absenden greift und nicht erst, wenn
   * der Coach fertig geantwortet hat.
   */
  hatEigenenBeitrag: boolean
}

/**
 * The pieces a caller gets when it lays the conversation out itself: the
 * message list and the input bar as ready-made nodes, plus the state it needs
 * to decide between an opening screen and the running exchange.
 */
export type CoachThreadFrame = {
  thread: ReactNode
  composer: ReactNode
  api: CoachThreadApi
  /**
   * False from the moment the first message is on its way, not only once it
   * has arrived: the screen has to move when the advisor presses send, and
   * creating the thread first takes a round trip.
   */
  isEmpty: boolean
}

type Props = {
  scope: CoachThreadScope
  /** False keeps the composer locked, e.g. until a feedback exists. */
  ready?: boolean
  empty: {
    /** Kleine Zeile über dem Titel, im Begrüßungsblock. */
    eyebrow?: string
    /**
     * Was ein Klick auf einen Vorschlag tut.
     *
     * `send` verschickt ihn als Nachricht — richtig für fertige Fragen an den
     * Coach. `prefill` setzt ihn ins Eingabefeld, damit der Satz zu Ende
     * geschrieben wird. Ein Satzanfang wie „Mir ist gelungen, dass …" als
     * Nachricht abzuschicken wäre ein halber Satz.
     */
    promptMode?: "send" | "prefill"
    title: string
    hint?: string
    /** Anklickbare Öffner. Ohne sie zeigt der Block nur Titel und Hinweis. */
    prompts?: CoachPrompt[]
    /**
     * Centres the empty state around a portrait, for a chat that owns the whole
     * screen. The coach introduces himself here instead of in a page header, so
     * nothing is left over once the conversation starts.
     */
    hero?: { portraitUrl?: string; name: string; role: string }
    /**
     * Unter dem Hinweis: der zweite Weg ins Gespräch, der Anruf.
     *
     * Im Begrüßungsblock und nicht unter dem Verlauf, weil er dort neben der
     * Frage steht, die er beantwortet. Sobald geschrieben oder gesprochen
     * wurde, ist der Block weg und der Knopf im Eingabefeld übernimmt.
     */
    actions?: ReactNode
    /**
     * Bubbles of its own under the greeting block, for an opening the coach
     * conducts in the thread itself (the exam drill asks for area and target
     * this way). Without a `hero` they stand alone at the top of the thread.
     */
    intro?: ReactNode
  }
  placeholder: { ready: string; blocked?: string }
  /** Persisted turns, replayed when a review or debrief is reopened. */
  initialMessages?: Array<
    ChatMessage & {
      mode?: ChatMode
      toolCalls?: ToolCallRecord[]
      coachStyleId?: string
    }
  >
  /** Sent once on mount — the question typed on the start page. */
  initialPrompt?: string
  /** Called once `initialPrompt` has been picked up, so the page can take it
   *  out of the URL. */
  onInitialPromptTaken?: () => void
  /** Merged into every request body; read fresh per send, so a mode switch
   *  always travels with its current value. */
  extraBody?: () => Record<string, unknown>
  /** Stamped on outgoing messages so a switch mid-thread is visible at once,
   *  not only after a reload. */
  mode?: ChatMode
  /** Awaited before the request goes out — creating the thread the turn will
   *  be stored in, or bringing a collapsed conversation back into view. May be
   *  synchronous; throwing keeps the text in the composer. */
  beforeSend?: () => unknown
  /**
   * "inner": own scroll box with auto-scroll, for a chat that fills the screen.
   * "page": document flow with a sticky composer and no auto-scroll, so a
   * reader working through a debrief above keeps control of the viewport.
   */
  scroll: "inner" | "page"
  /**
   * Horizontal padding inside the scroll box, for a thread that sits in a panel
   * of its own instead of a page column. Normally the page around it provides
   * the margin; where it does not, bubbles and chips would touch the frame.
   */
  inset?: boolean
  /**
   * Changes whenever content below the thread changes, so the auto-scroll
   * follows it. Needed because `afterThread` content is not a message and the
   * effect would otherwise never fire for it.
   */
  scrollSignal?: string | number
  /**
   * Scrolls with the thread, above it — the feedback panel.
   *
   * As a function it also gets a handle on the conversation, so something up
   * there (the coach callout over the report) can ask a question or put the
   * cursor in the composer without owning a second chat.
   */
  aboveThread?: ReactNode | ((api: CoachThreadApi) => ReactNode)
  /** Unter dem Verlauf, über dem Eingabefeld — die Aktionen zum Einstieg. */
  afterThread?: ReactNode | ((api: CoachThreadApi) => ReactNode)
  /**
   * Direkt über einer einzelnen Nachricht — die Feedback-Karte über dem
   * Abschluss des Coaches. An der Nachricht, nicht am Ende des Verlaufs: nach
   * dem Abschluss kann weitergeschrieben werden, und die Karte soll dort
   * stehen bleiben, wo der Coach das Feedback übergeben hat. Darunter, weil
   * er erst spricht und die Bilanz zusammenfasst, was er gesagt hat — dieselbe
   * Folge, in der beides aus dem Strom eintrifft.
   */
  afterMessage?: (message: UIMessage) => ReactNode
  /** Above the composer inside the pinned bar — reference picker, mode switch. */
  composerAccessory?: ReactNode | ((api: CoachThreadApi) => ReactNode)
  /** A feedback excerpt attached to the next message as a blockquote — shown
   *  as a removable chip above the composer until it is sent. */
  quote?: string | null
  onQuoteClear?: () => void
  /** The coach this conversation was held with, so a reopened thread keeps its
   *  own face after the advisor switched coaches. */
  coachStyleId?: string
  /** Inside the input, left of the send button — the call button. As a
   *  function it can step aside while the opening screen offers the same
   *  thing more prominently. */
  composerAction?: ReactNode | ((ctx: { isEmpty: boolean }) => ReactNode)
  /** Right of the composer — the phase's "next" CTA. */
  trailing?: ReactNode | ((api: CoachThreadApi) => ReactNode)
  onFinish?: (message: UIMessage) => void
  /**
   * Replaces the built-in layout. The start page arranges the same thread and
   * composer around its own stage, and without this slot it would have to
   * duplicate the sending logic to do so.
   */
  frame?: (parts: CoachThreadFrame) => ReactNode
  /**
   * Where the pinned bar renders, when it does not belong to this column.
   *
   * The review puts input and CTA under the conversation AND the feedback
   * sheet, so the bar has to leave the column it is written in. Passing the
   * prop hands it over — `null` while the target element is not mounted yet,
   * which is why an absent prop and `null` mean different things here.
   */
  barSlot?: HTMLElement | null
  /** "md" for a chat that owns the screen; the default is the quieter bar. */
  barSize?: "sm" | "md"
  autoFocus?: boolean
}

function endpointFor(scope: CoachThreadScope): string {
  if (scope.kind === "run") return `/api/process-runs/${scope.runId}/coach-chat`
  if (scope.kind === "session") return "/api/ai/coach-chat"
  return "/api/ai/coach-chat-general"
}

/** Which Socket.IO lane carries this scope. */
function laneFor(scope: CoachThreadScope): string {
  if (scope.kind === "run") return "run-coach"
  if (scope.kind === "session") return "coach"
  return "chat"
}

/**
 * What the lane needs beyond the messages.
 *
 * The HTTP routes carry the run id in the path; over one socket the lane has to
 * be told which run it is talking about.
 */
function socketBodyFor(scope: CoachThreadScope): Record<string, unknown> {
  return scope.kind === "run" ? { runId: scope.runId } : {}
}

export function CoachThread({
  scope,
  ready = true,
  empty,
  placeholder,
  initialMessages,
  initialPrompt,
  onInitialPromptTaken,
  extraBody,
  mode,
  beforeSend,
  scroll,
  inset = false,
  scrollSignal,
  aboveThread,
  afterThread,
  afterMessage,
  composerAccessory,
  quote,
  onQuoteClear,
  coachStyleId,
  composerAction,
  trailing,
  onFinish,
  frame,
  barSlot,
  barSize = "sm",
  autoFocus,
}: Props) {
  const seeded = useMemo<UIMessage[]>(
    () =>
      (initialMessages ?? []).map((m, i) => ({
        id: m.id ?? `seed-${i}-${m.role}`,
        role: m.role,
        parts: [
          // Replayed as finished tool parts, so the proposal cards come back
          // with the answer they belong to.
          ...(m.toolCalls ?? []).map(
            (call) =>
              ({
                type: `tool-${call.toolName}`,
                toolCallId: call.toolCallId,
                state: "output-available",
                input: call.input,
                output: call.output,
              }) as UIMessage["parts"][number]
          ),
          { type: "text", text: m.content },
        ],
        metadata:
          m.mode || m.istAbschluss || m.coachStyleId
            ? {
                mode: m.mode,
                istAbschluss: m.istAbschluss,
                coachStyleId: m.coachStyleId,
              }
            : undefined,
      })),
    [initialMessages]
  )

  // The transport is built once, but the extra body changes with the UI; a ref
  // keeps every send on the current value instead of the mount-time one.
  const extraBodyRef = useRef(extraBody)
  extraBodyRef.current = extraBody

  const [transport] = useState(
    () =>
      new FallbackChatTransport({
        lane: laneFor(scope),
        api: endpointFor(scope),
        body: () => ({
          ...socketBodyFor(scope),
          ...extraBodyRef.current?.(),
        }),
      })
  )

  const { messages, sendMessage, status, stop } = useChat({
    transport,
    messages: seeded,
    onFinish: ({ message }) => onFinish?.(message),
    // Without this a failed turn leaves the advisor's own bubble standing with
    // no reply and no explanation.
    onError: () =>
      toast.error(
        "Der Coach konnte nicht antworten. Bitte noch einmal senden."
      ),
  })
  const [input, setInput] = useState("")
  // Appended, not replaced: dictating twice in a row should add to what is
  // already there instead of dropping it.
  const dictation = useDictation((text) =>
    setInput((prev) => (prev ? `${prev.trimEnd()} ${text}` : text))
  )
  const bottomRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const initialPromptSent = useRef(false)

  // The text on its way out while `beforeSend` still runs. Without it there
  // would be a blank moment between pressing send and the bubble appearing.
  const [pending, setPending] = useState<string | null>(null)

  const isStreaming = status === "streaming" || status === "submitted"
  const canSend = ready && !isStreaming && pending === null

  // Eine neue Nachricht ist eine Bewegung im Gespräch: weich hinterher.
  useEffect(() => {
    if (scroll !== "inner" || messages.length === 0) return
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages.length, status, scroll])

  /**
   * Inhalt unter dem Verlauf, der keine Nachricht ist.
   *
   * Der gestreamte Abschluss des Coaches und die Bilanz der Einheit kommen über
   * `afterThread` herein. Ohne eigenes Signal ändert sich weder
   * `messages.length` noch `status` — wer „Feedback erstellen" drückt, sieht
   * dann schlicht nichts passieren, weil alles unterhalb des Sichtfensters
   * wächst.
   *
   * Hart statt weich: der Text wächst Zeichen für Zeichen, und eine weiche
   * Animation startet bei jedem Token neu, statt jemals unten anzukommen. Der
   * Preis ist, dass ein Hochscrollen während des Streams zurückgezogen wird —
   * vertretbar für die paar Sekunden, in denen die Bilanz entsteht.
   */
  useEffect(() => {
    if (scroll !== "inner" || scrollSignal === undefined) return
    bottomRef.current?.scrollIntoView({ behavior: "auto", block: "end" })
  }, [scrollSignal, scroll])

  useEffect(() => {
    const prompt = initialPrompt?.trim()
    if (!prompt || initialPromptSent.current) return
    initialPromptSent.current = true
    onInitialPromptTaken?.()
    void send(prompt)
    // send is recreated every render; the ref above is what guards the once.
  }, [initialPrompt])

  const send = async (text: string) => {
    const trimmed = text.trim()
    if (!trimmed || !canSend) return
    setInput("")
    setPending(trimmed)
    try {
      await beforeSend?.()
    } catch {
      // beforeSend reports its own failure; put the text back so the advisor
      // does not have to retype it.
      setPending(null)
      setInput(trimmed)
      return
    }
    // The quote is folded into the content itself so it persists and replays
    // without any schema change; CoachBubble re-derives the styled block from
    // the raw text. Cleared only after dispatch — a beforeSend failure above
    // keeps the chip alongside the restored input.
    const outgoing = quote ? composeQuotedMessage(quote, trimmed) : trimmed
    void sendMessage(
      mode ? { text: outgoing, metadata: { mode } } : { text: outgoing }
    )
    setPending(null)
    onQuoteClear?.()
  }

  // Only the advisor's own turns carry a mode; the coach's reply belongs to the
  // same section, so the last known mode is carried forward. Without this the
  // divider would repeat on every question.
  /** Schreibt der Coach an dieser Nachricht gerade noch? */
  const laeuftNoch = (m: UIMessage, idx: number) =>
    m.role !== "user" && idx === messages.length - 1 && isStreaming

  let carried: ChatMode | undefined
  const modeAt = messages.map((m) => {
    carried = (m.metadata as { mode?: ChatMode } | undefined)?.mode ?? carried
    return carried
  })

  const api: CoachThreadApi = {
    ask: (text) => void send(text),
    focusComposer: () => {
      const field = inputRef.current
      field?.scrollIntoView({ behavior: "smooth", block: "nearest" })
      field?.focus()
    },
    canSend,
    // `pending` counts because the sent question is already visible as a bubble
    // even before it appears in `messages`.
    isEmpty: messages.length === 0 && pending === null,
    hatEigenenBeitrag:
      pending !== null || messages.some((m) => m.role === "user"),
  }
  const loese = (slot: ReactNode | ((api: CoachThreadApi) => ReactNode)) =>
    typeof slot === "function" ? slot(api) : slot

  const above = loese(aboveThread)
  const after = loese(afterThread)
  const accessory = loese(composerAccessory)
  const next = loese(trailing)

  const hero = empty.hero
  const sent = messages
    .filter((message) => message.role === "user")
    .map(partsToText)
  if (pending !== null) sent.push(pending)
  /**
   * Zwei Sorten Vorschläge, und sie gehören an verschiedene Orte.
   *
   * Anschlussfragen beziehen sich auf die letzte Antwort und stehen deshalb
   * am Ende des Verlaufs, wo sie mitscrollen. Einstiegsfragen gehören ans
   * Eingabefeld, aber nur solange noch nichts gesagt wurde — danach sind sie
   * Rauschen.
   *
   * Der Unterschied ist nicht kosmetisch: in der fixierten Leiste kostete jede
   * Zeile Chips den Verlauf dauerhaft Höhe, und in einer schmalen Spalte
   * bricht jede Frage auf eine eigene Zeile um.
   */
  const verlaufLeer = messages.length === 0 && pending === null
  const anschlussfragen = coachSuggestions(messages)
  // The cap is for suggestions only. Action chips are the answers to a
  // question on screen, and cutting one off would drop a choice.
  const startfragen: CoachPrompt[] = verlaufLeer
    ? (empty.prompts ?? []).some(istAktion)
      ? (empty.prompts ?? [])
      : (empty.prompts ?? []).slice(0, 5)
    : []

  // In the exam drill the server pins "Nächste Frage" as the first follow-up
  // chip (see `ersterVorschlag` in the stream service). It is the way forward,
  // the other chips are detours, so it alone gets the primary look.
  const ersterChipPrimaer = mode === "pruefung"

  const chipListe = (
    eintraege: CoachPrompt[],
    className: string,
    tour?: string,
    primaerErster = false
  ) =>
    eintraege.length ? (
      <div
        role="group"
        aria-label="Antwortvorschläge"
        data-tour={tour}
        className={cn("flex flex-wrap gap-2", className)}
      >
        {eintraege.map((entry, index) => {
          const label = typeof entry === "string" ? entry : entry.label
          // Ein Ziel-Chip verlässt das Gespräch, statt hineinzuschreiben. Er
          // darf deshalb auch nicht an `canSend` hängen: dass der Coach gerade
          // schreibt, ist kein Grund, den Weg nach nebenan zu sperren.
          if (istZiel(entry)) {
            return (
              <Button
                key={entry.to}
                asChild
                variant="outline"
                size="sm"
                className="h-auto max-w-full rounded-full py-1.5 text-left font-normal whitespace-normal"
              >
                <Link to={entry.to}>{label}</Link>
              </Button>
            )
          }
          if (istAktion(entry)) {
            return (
              <Button
                key={entry.label}
                type="button"
                variant={entry.primaer ? "default" : "outline"}
                size="sm"
                onClick={() => entry.aktion((text) => void send(text))}
                disabled={!canSend}
                className="h-auto max-w-full rounded-full py-1.5 text-left font-normal whitespace-normal"
              >
                {label}
              </Button>
            )
          }
          const prompt = typeof entry === "string" ? entry : entry.prompt
          return (
            <Button
              key={prompt}
              type="button"
              variant={primaerErster && index === 0 ? "default" : "outline"}
              size="sm"
              onClick={() => {
                if (
                  sent.length === 0 &&
                  empty.promptMode === "prefill" &&
                  empty.prompts?.includes(entry)
                ) {
                  // Remove the trailing ellipsis so the advisor can finish the
                  // sentence without including the continuation cue in the message.
                  setInput(prompt.replace(/\s*…\s*$/, " "))
                  inputRef.current?.focus()
                  return
                }
                void send(prompt)
              }}
              disabled={!canSend}
              className="h-auto max-w-full rounded-full py-1.5 text-left font-normal whitespace-normal"
            >
              {label}
            </Button>
          )
        })}
      </div>
    ) : null

  /** Am Ende des Verlaufs, im Scroll-Bereich. */
  const anschlussChips = chipListe(
    anschlussfragen,
    "mt-5",
    undefined,
    ersterChipPrimaer
  )
  /** Über dem Eingabefeld, nur im leeren Schirm. */
  const startChips = chipListe(startfragen, "mb-3", "chat-vorschlaege")

  // Exactly one scroll anchor per layout. The built-in layout places it below
  // the follow-up chips, so the auto-scroll brings them into view as well.
  // Only a frame gets the anchor inside the thread, because there the chips
  // move out of the scroll box into the composer. Mounting both on one ref
  // would let whichever mounted last win — in a conversation started from the
  // empty screen that is the one above the chips, and they end up below the
  // fold after every answer.
  const anchorInThread = frame ? <div ref={bottomRef} /> : null

  // Der Verlauf liegt im Kontext, damit eine Karte in einer Nachricht selbst
  // senden kann. Nur der Verlauf, nicht das Eingabefeld: dort gibt es nichts,
  // was tiefer als eine Ebene reicht.
  const thread = (
    <CoachThreadApiProvider value={api}>
      {messages.length === 0 && pending !== null ? (
        <div className="flex flex-col gap-5 py-5">
          <CoachBubble role="user" text={pending} coachStyleId={coachStyleId} />
          <CoachBubble
            role="assistant"
            text=""
            typing
            coachStyleId={coachStyleId}
          />
          {anchorInThread}
        </div>
      ) : messages.length === 0 && empty.intro && !hero ? (
        <div className="flex flex-col gap-5 py-5">{empty.intro}</div>
      ) : messages.length === 0 ? (
        hero ? (
          <div
            className={cn(
              "reveal flex flex-col items-center px-4 text-center",
              // The bubbles below need the thread's width, not the title's.
              empty.intro && "w-full"
            )}
          >
            <Avatar className="size-20 border border-[var(--rule)]">
              {hero.portraitUrl ? (
                <AvatarImage src={hero.portraitUrl} alt="" />
              ) : null}
              <AvatarFallback className="bg-accent text-[20px] font-medium text-primary">
                {initialsOf(hero.name)}
              </AvatarFallback>
            </Avatar>
            <div className="mt-3 flex items-baseline gap-2">
              <span className="text-[16px] font-medium">{hero.name}</span>
              <span className="text-[14px] text-muted-foreground">
                {hero.role}
              </span>
            </div>
            {empty.eyebrow && (
              <span className="mt-4 text-[11.5px] font-bold tracking-[0.06em] text-muted-foreground uppercase">
                {empty.eyebrow}
              </span>
            )}
            <h3
              className={cn(
                "font-display text-[20px] leading-snug font-medium tracking-tight",
                empty.eyebrow ? "mt-1.5" : "mt-4"
              )}
            >
              {empty.title}
            </h3>
            {empty.hint && (
              <p className="mt-1.5 max-w-[520px] text-[13.5px] leading-relaxed text-muted-foreground">
                {empty.hint}
              </p>
            )}
            {empty.actions && (
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2">
                {empty.actions}
              </div>
            )}
            {empty.intro && (
              <div className="mt-8 flex w-full flex-col gap-5 text-left">
                {empty.intro}
              </div>
            )}
          </div>
        ) : (
          <div className="reveal py-5">
            <h3 className="font-display text-[18px] font-medium tracking-tight">
              {empty.title}
            </h3>
            {empty.hint && (
              <p className="mt-1 text-[13.5px] leading-relaxed text-muted-foreground">
                {empty.hint}
              </p>
            )}
            {empty.actions && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {empty.actions}
              </div>
            )}
          </div>
        )
      ) : (
        <div className="flex flex-col gap-5 py-5">
          {messages.map((m, idx) => {
            const turnMode = modeAt[idx]
            return (
              <div key={m.id} className="contents">
                {/* Only where a section starts, not on every bubble: a reopened
                  thread has to explain why the coach suddenly asks. The very
                  first section is only labelled when it is training — an
                  opening free exchange needs no announcement. */}
                {turnMode &&
                  turnMode !== modeAt[idx - 1] &&
                  (idx > 0 || turnMode === "training") && (
                    <div className="flex items-center gap-3 pt-1">
                      <span className="h-px flex-1 bg-[var(--rule)]" />
                      <span className="text-[11.5px] text-muted-foreground">
                        {CHAT_MODE_LABELS[turnMode]}
                      </span>
                      <span className="h-px flex-1 bg-[var(--rule)]" />
                    </div>
                  )}
                <CoachBubble
                  role={m.role === "user" ? "user" : "assistant"}
                  text={partsToText(m)}
                  tools={
                    hasToolCalls(m) ? <CoachToolCalls message={m} /> : undefined
                  }
                  attachments={
                    // Erst der Text, dann die Karte. Das Werkzeug ist vor dem
                    // ersten Wort fertig; stünde die Karte schon da, schöbe
                    // jede gestreamte Zeile sie weiter nach unten. Die Pillen
                    // darüber bleiben — sie sind eine Zeile hoch und sagen,
                    // dass gerade etwas nachgeschlagen wird.
                    laeuftNoch(m, idx) || !hasToolCards(m) ? undefined : (
                      <CoachToolCards message={m} />
                    )
                  }
                  coachStyleId={
                    // The answer's own coach wins over the thread's: in an
                    // open chat the advisor may have switched since.
                    (m.metadata as { coachStyleId?: string } | undefined)
                      ?.coachStyleId ?? coachStyleId
                  }
                  streaming={laeuftNoch(m, idx)}
                />
                {afterMessage?.(m)}
              </div>
            )
          })}
          {isStreaming && messages[messages.length - 1]?.role === "user" && (
            <CoachBubble
              role="assistant"
              text=""
              typing
              coachStyleId={coachStyleId}
            />
          )}
          {anchorInThread}
        </div>
      )}
    </CoachThreadApiProvider>
  )

  const composer = (
    <div className="w-full">
      {startChips}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <ChatBar
          className="flex-1"
          size={barSize}
          autoFocus={autoFocus}
          inputRef={inputRef}
          value={input}
          onChange={setInput}
          onSubmit={(text) => void send(text ?? input)}
          placeholder={ready ? placeholder.ready : (placeholder.blocked ?? "")}
          disabled={!ready}
          busy={isStreaming}
          onStop={() => stop()}
          dictation={dictation}
          actions={
            typeof composerAction === "function"
              ? composerAction({
                  isEmpty: messages.length === 0 && pending === null,
                })
              : composerAction
          }
          // The quote chip rides in ChatBar's `above` slot, directly over the
          // input it will be sent with; the accessory (mode switch) sits above it.
          above={
            accessory || quote ? (
              <>
                {accessory}
                {quote && (
                  <div className="mb-2 flex items-start gap-2 rounded-md border border-[var(--rule)] bg-card px-3 py-2">
                    <TextQuote className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    <p className="line-clamp-2 flex-1 text-[13px] text-muted-foreground italic">
                      {quote}
                    </p>
                    <Button
                      type="button"
                      size="icon"
                      variant="ghost"
                      onClick={onQuoteClear}
                      aria-label="Zitat entfernen"
                      className="h-5 w-5 shrink-0"
                    >
                      <X className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </>
            ) : undefined
          }
        />

        {next && (
          <>
            <span className="hidden h-6 w-px shrink-0 bg-[var(--rule)] sm:block" />
            <div className="shrink-0">{next}</div>
          </>
        )}
      </div>
    </div>
  )

  // The rule stays full width, the padding sits inside it.
  const bar = (
    <div
      className={cn(
        "no-print shrink-0 border-t border-[var(--rule)] bg-[var(--paper)]/90 py-3 backdrop-blur",
        inset && "px-4"
      )}
    >
      {composer}
    </div>
  )

  if (frame) {
    return (
      <>
        {frame({
          thread,
          // Der Rahmen baut sein Layout selbst und kennt nur diese zwei Knoten.
          // Er bekommt deshalb beide Chip-Gruppen an einer Stelle — sonst
          // verschwänden die Anschlussfragen dort stillschweigend.
          composer: (
            <>
              {anschlussChips}
              {composer}
            </>
          ),
          isEmpty: api.isEmpty,
          api,
        })}
      </>
    )
  }

  if (scroll === "page") {
    return (
      <div>
        <div className="no-print">{thread}</div>
        {anschlussChips && <div className="no-print">{anschlussChips}</div>}
        <div className="no-print sticky bottom-0 border-t border-[var(--rule)] bg-[var(--paper)]/90 py-3 backdrop-blur">
          {composer}
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        {/* Der Begrüßungsblock sitzt mittig im leeren Schirm; sobald das
            Gespräch läuft, ist er weg und der Verlauf fließt von oben.

            FALLE: `flex-col` ist tragend. Ohne es landete alles, was über dem
            Verlauf steht, als zweite Spalte NEBEN dem Begrüßungsblock — bis zum
            dialogischen Feedback fiel das nicht auf, weil `hero` nur im offenen
            Chat genutzt wurde und der nichts über dem Verlauf zeigt. */}
        <div
          className={cn(
            "flex flex-col",
            inset && "px-4",
            hero && messages.length === 0 ? "min-h-full py-8" : "pb-4"
          )}
        >
          {above}
          {above ? (
            <div
              className={cn(
                "no-print",
                // Der Begrüßungsblock nimmt den Restraum und zentriert sich
                // darin; ein laufender Verlauf beginnt direkt unter dem, was
                // über ihm steht.
                hero && messages.length === 0
                  ? "flex min-h-0 flex-1 items-center justify-center"
                  : "mt-6"
              )}
            >
              {thread}
            </div>
          ) : hero && messages.length === 0 ? (
            <div className="flex min-h-0 flex-1 items-center justify-center">
              {thread}
            </div>
          ) : (
            thread
          )}
          {after && <div className="no-print mt-5">{after}</div>}
          {anschlussChips && <div className="no-print">{anschlussChips}</div>}
          <div ref={bottomRef} />
        </div>
      </div>
      {/* The rule stays full width, the padding sits inside it. */}
      {barSlot === undefined ? bar : barSlot && createPortal(bar, barSlot)}
    </div>
  )
}
